import { Children, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, IconButton } from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNewRounded';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIosRounded';

const arrowButtonSx = {
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  zIndex: 1,
  bgcolor: 'background.paper',
  boxShadow: 3,
  '&:hover': { bgcolor: 'background.paper' },
};

// Carrusel horizontal de productos: scroll nativo con snap (funciona con touch/trackpad)
// mas flechas que avanzan una "pagina" a la vez; las flechas se ocultan en los extremos.
export default function ProductCarousel({ children }) {
  const scrollerRef = useRef(null);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const updateArrows = () => {
    const el = scrollerRef.current;
    if (!el) {
      return;
    }
    setCanScrollPrev(el.scrollLeft > 4);
    setCanScrollNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };

  useEffect(() => {
    updateArrows();
    const el = scrollerRef.current;
    if (!el) {
      return undefined;
    }

    el.addEventListener('scroll', updateArrows, { passive: true });
    window.addEventListener('resize', updateArrows);
    return () => {
      el.removeEventListener('scroll', updateArrows);
      window.removeEventListener('resize', updateArrows);
    };
    // Recalcula cuando cambian los productos (largo distinto -> distinto scrollWidth).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [children]);

  const scrollByPage = (direction) => {
    const el = scrollerRef.current;
    if (!el) {
      return;
    }
    el.scrollBy({ left: direction * el.clientWidth * 0.9, behavior: 'smooth' });
  };

  return (
    <Box sx={{ position: 'relative' }}>
      {canScrollPrev && (
        <IconButton aria-label="Anterior" onClick={() => scrollByPage(-1)} sx={{ ...arrowButtonSx, left: -18 }}>
          <ArrowBackIosNewIcon fontSize="small" />
        </IconButton>
      )}

      <Box
        ref={scrollerRef}
        sx={{
          display: 'flex',
          gap: 3,
          overflowX: 'auto',
          scrollSnapType: 'x mandatory',
          scrollBehavior: 'smooth',
          py: 0.5,
          '&::-webkit-scrollbar': { display: 'none' },
          scrollbarWidth: 'none',
        }}
      >
        {Children.map(children, (child) => (
          <Box
            sx={{
              flex: '0 0 auto',
              width: { xs: '75%', sm: '46%', md: '23%' },
              scrollSnapAlign: 'start',
            }}
          >
            {child}
          </Box>
        ))}
      </Box>

      {canScrollNext && (
        <IconButton aria-label="Siguiente" onClick={() => scrollByPage(1)} sx={{ ...arrowButtonSx, right: -18 }}>
          <ArrowForwardIosIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
}

ProductCarousel.propTypes = {
  children: PropTypes.node.isRequired,
};
